import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { InspectionPill as Pill } from '@/components/inspections/InspectionChip';
import { InspectionPhoto } from '@/components/inspections/InspectionPhoto';
import { cn } from '@/lib/utils';
import {
    GRADE_TONES,
    RESULT_LABELS,
    RESULT_TONES,
    SLOT_LABELS,
    SLOT_ORDER,
    formatKm,
    formatKmChange,
    itemLabel,
    lineGradeLabel,
    longDay,
    monthName,
    splitVisitLines,
    type InspectionChecklist,
    type InspectionVisit,
    type InspectionVisitPhoto,
} from '@/lib/inspections';

function visitTitle(visit: InspectionVisit): string {
    if (visit.kind === 'monthly') {
        const round = monthName(visit.period);
        return round ? `${round} monthly inspection` : 'Monthly inspection';
    }
    if (visit.kind === 'recheck') return 'Re-check';
    return 'Inspection notes';
}

function ContractMarker({ visit, contractId }: { visit: InspectionVisit; contractId: number }) {
    if (visit.between_contracts) return <Pill tone="info">Between contracts</Pill>;
    if (visit.contract_id === contractId) return <Pill tone="neutral">This contract</Pill>;
    return <Pill tone="neutral">Contract #{visit.contract_id}</Pill>;
}

interface InspectionCertificateProps {
    visit: InspectionVisit;
    checklist: InspectionChecklist;
    /** The contract the page was opened on, so its own visits read "This contract". */
    contractId: number;
    /** Open the six angle shots without a tap: the newest visit only. */
    defaultShowPhotos?: boolean;
}

/**
 * One visit in the car's history: what it found, who looked, and the photos. The lines that need
 * reading (Must fix, Fix soon, Fixed, No fix needed) are listed with their notes and photos; the OK
 * lines fold away under a count, because a clean monthly is thirteen of them.
 *
 * Not the visit's free-text remarks: plan section 8 does not put them on the partner's certificate,
 * and no capture chip keeps money or a driver's details out of them (see `src/lib/inspections.ts`).
 * A legacy row therefore shows its date, its contract and "Notes only".
 */
export function InspectionCertificate({ visit, checklist, contractId, defaultShowPhotos = false }: InspectionCertificateProps) {
    const [showPhotos, setShowPhotos] = useState(defaultShowPhotos);
    const { flagged, ok } = splitVisitLines(visit.lines);

    const photosByLine = new Map<number, InspectionVisitPhoto[]>();
    for (const photo of visit.photos) {
        if (photo.line_id === null) continue;
        photosByLine.set(photo.line_id, [...(photosByLine.get(photo.line_id) ?? []), photo]);
    }
    const slotPhotos = visit.photos
        .filter((p): p is InspectionVisitPhoto & { slot: NonNullable<InspectionVisitPhoto['slot']> } => p.slot !== null)
        .sort((a, b) => SLOT_ORDER.indexOf(a.slot) - SLOT_ORDER.indexOf(b.slot));

    const odometer = formatKm(visit.odometer_km);
    const odometerChange = formatKmChange(visit.odometer_change_km);
    const lastService = formatKm(visit.last_service_km);
    const facts = [
        visit.inspector_first_name ? `Inspector: ${visit.inspector_first_name}` : null,
        // With the "Notes only" result beside it: "Notes only — recorded before the checklist".
        visit.kind === null ? 'Recorded before the checklist' : null,
    ].filter(Boolean);

    return (
        <Card>
            <CardContent className="space-y-4 p-4 sm:p-6">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                        <h3 className="font-semibold">{visitTitle(visit)}</h3>
                        <p className="text-sm text-muted-foreground">
                            {/* The Ghana day the server derived, day first like the status card's '3 Dec'. */}
                            {longDay(visit.inspected_on) ?? '-'}
                            {facts.length > 0 && ` · ${facts.join(' · ')}`}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 sm:justify-end">
                        <Pill tone={RESULT_TONES[visit.result]}>{RESULT_LABELS[visit.result]}</Pill>
                        {visit.late && <Pill tone="warning">Late</Pill>}
                        {visit.released && <Pill tone="success">Released</Pill>}
                        <ContractMarker visit={visit} contractId={contractId} />
                    </div>
                </div>

                {(odometer || lastService) && (
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        {odometer && (
                            <div>
                                <dt className="text-xs text-muted-foreground">Odometer</dt>
                                <dd className="font-medium tabular-nums">
                                    {odometer}
                                    {odometerChange && (
                                        <span className="ml-1 text-xs font-normal text-muted-foreground">({odometerChange})</span>
                                    )}
                                </dd>
                            </div>
                        )}
                        {lastService && (
                            <div>
                                <dt className="text-xs text-muted-foreground">Last service at</dt>
                                <dd className="font-medium tabular-nums">{lastService}</dd>
                            </div>
                        )}
                    </dl>
                )}

                {flagged.length > 0 && (
                    <ul className="space-y-2">
                        {flagged.map(line => {
                            const label = itemLabel(checklist, line.item);
                            const linePhotos = photosByLine.get(line.id) ?? [];
                            return (
                                <li key={line.id} className="space-y-2 rounded-md border p-3">
                                    <div className="flex items-start justify-between gap-3">
                                        <p className="text-sm font-medium">{label}</p>
                                        <Pill tone={GRADE_TONES[line.grade]} className="shrink-0">{lineGradeLabel(line)}</Pill>
                                    </div>
                                    {line.note && <p className="text-sm">{line.note}</p>}
                                    {linePhotos.length > 0 && (
                                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                                            {linePhotos.map(photo => (
                                                <InspectionPhoto key={photo.id} url={photo.url} label={label} />
                                            ))}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}

                {ok.length > 0 && (
                    <details className="group rounded-md border">
                        <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-2 px-3 py-2 text-sm [&::-webkit-details-marker]:hidden">
                            <span>
                                {ok.length === 1 ? '1 line OK' : `${ok.length} lines OK`}
                            </span>
                            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden="true" />
                        </summary>
                        <ul className="space-y-1 border-t px-3 py-2 text-sm text-muted-foreground">
                            {ok.map(line => <li key={line.id}>{itemLabel(checklist, line.item)}</li>)}
                        </ul>
                    </details>
                )}

                {slotPhotos.length > 0 && (
                    <div className="space-y-2">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="-ml-2 h-11 gap-1 px-2 text-sm"
                            aria-expanded={showPhotos}
                            onClick={() => setShowPhotos(open => !open)}
                        >
                            {showPhotos ? 'Hide the car photos' : `Show the car photos (${slotPhotos.length})`}
                            <ChevronDown className={cn('h-4 w-4 transition-transform', showPhotos && 'rotate-180')} aria-hidden="true" />
                        </Button>
                        {showPhotos && (
                            <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                                {slotPhotos.map(photo => (
                                    <figure key={photo.id} className="space-y-1">
                                        <InspectionPhoto url={photo.url} label={SLOT_LABELS[photo.slot]} />
                                        <figcaption className="truncate text-xs text-muted-foreground">{SLOT_LABELS[photo.slot]}</figcaption>
                                    </figure>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
